import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { routePaths } from '@/app/routes';
import type { MaintenanceSection, MaintenanceSectionKey } from '../types/maintenance.types';
import { MaintenanceIcon, MaintenancePeriod } from './MaintenancePeriod';

interface SectionMaintenanceProps {
  sectionKey: Exclude<MaintenanceSectionKey, 'all'>;
  section: MaintenanceSection;
}

/**
 * In-app block for ONE section (results/medical record or appointments):
 * rendered inside the normal shell instead of the section's pages, so the
 * rest of the account stays usable.
 */
export function SectionMaintenance({ sectionKey, section }: SectionMaintenanceProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t(`maintenance.sections.${sectionKey}`)} />
      <Card className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-warning-light text-[#9a6700]">
          <MaintenanceIcon />
        </div>
        <div>
          <p className="text-lg font-bold text-ink">{t('maintenance.section_title')}</p>
          <p className="mt-2 max-w-prose whitespace-pre-line text-sm text-ink-soft">
            {section.message ?? t('maintenance.default_message')}
          </p>
          <MaintenancePeriod section={section} className="mt-3 text-sm text-ink-mute" />
        </div>
        <Link
          to={routePaths.home}
          className="inline-flex h-11 items-center justify-center rounded-input border border-muted-300 bg-white px-4 text-sm font-semibold text-ink transition-colors hover:bg-muted-100"
        >
          {t('maintenance.back_home')}
        </Link>
      </Card>
    </div>
  );
}
